<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" :subTitle="$t('master.textKaryawan')" />
      <PageHeader
        v-if="phaseOneShow"
        :title="title"
        :subTitle="$t('master.textKaryawan')"
        hasBreadcrumb
        hasBackButton
        :buttonText="$t('global.back')"
      />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack class="me-2" href="/master" />
          <McButton
            v-if="hasAccessAll(['users:create', 'd5a-default-mdkaryawan:read'])"
            variant="secondary"
            class="me-2"
            icon="icon-outline_upload"
            :text="$t('global.action.upload')"
            @click="$router.push({ name: 'Master Employee Import' })"
          />
          <b-dropdown
            v-if="phaseOneShow"
            right
            variant="outline-primary"
            menu-class="dropdown-menu-end"
            class="mc-btn-download me-2"
          >
            <template #button-content>
              {{ $t('global.action.download') }} <i class="mdi mdi-chevron-down ps-2" />
            </template>
            <b-dropdown-item>
              <i class="bx bx-book-content align-middle pe-1" />
              {{ $t('global.action.downloadExcel') }}
            </b-dropdown-item>
            <b-dropdown-item>
              <i class="bx bx-book-content align-middle pe-1" />
              {{ $t('global.action.downloadPdf') }}
            </b-dropdown-item>
          </b-dropdown>
          <McButton
            v-if="hasAccessAll(['users:create', 'd5a-default-mdkaryawan:read']) && !noData"
            icon="icon-outline_plus"
            :text="$t('global.create')"
            @click="$router.push({ name: 'Master Employee Create' })"
          />
        </div>
      </div>
    </div>
    <McGrapqlTable
      :apiClient="query"
      :fields="fields"
      :customHandlers="customHandlers"
      :activeFilters="activeFilters"
      :hasFilter="true"
      :hasDelete="false"
      :isEmpty="noData"
      :busy="isLoading"
      :refreshData="!isLoading"
      trackBy="userId"
      hasCheckbox
      searchable
      withCard
    >
      <template #button-filter="{ isSelected, selectedIds }">
        <div v-if="!noData" class="d-flex justify-content-between">
          <McInlineFilter
            :filterItems="filterStatus"
            :activeKeys="activeFilters['isActive']"
            :hasSelectAll="false"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'isActive', values: $event }])"
          />
          <b-dropdown
            v-if="hasAccessAll(['users:update', 'd5a-default-mdkaryawan:read'])"
            variant="outline-primary"
            right
            :disabled="!isSelected"
          >
            <template #button-content>
              {{ $t('global.action.changeStatus') }}
              <i class="mdi mdi-chevron-down" />
            </template>
            <b-dropdown-item @click="changeStatus('active', selectedIds)">
              {{ $t('global.active') }}
            </b-dropdown-item>
            <b-dropdown-item @click="changeStatus('inactive', selectedIds)">
              {{ $t('global.nonActive') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
      <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataFields"
          @setColumn="handleSetDataFields"
        />
      </template>
      <template #cell(isActive)="data">
        <span v-if="!data.item.isActive" class="mc-status mc-status--grey">
          {{ $t('global.nonActive') }}
        </span>
        <span v-else class="mc-status mc-status--green"> {{ $t('global.active') }} </span>
      </template>
      <template
        #cell(identification.methods.faceRecognition)="data"
        v-if="hasScopeRfidAndFaceRecognition"
      >
        <span
          v-if="getFaceRecognitionStatus(data.item) === FACE_RECOGNITION_STATUS_LABEL.WITH_PHOTO"
        >
          <i class="mc-fa icon-fill_checkmark-circle me-2 green-500"></i>
          {{ $t('rfidAndFacerecognition.photo.success') }}
        </span>
        <span
          v-else-if="getFaceRecognitionStatus(data.item) === FACE_RECOGNITION_STATUS_LABEL.VALID"
        >
          <i class="mc-fa icon-fill_checkmark-circle me-2 green-500"></i>
          {{ $t('rfidAndFacerecognition.photo.success') }}
        </span>
        <span
          v-else-if="getFaceRecognitionStatus(data.item) === FACE_RECOGNITION_STATUS_LABEL.PENDING"
        >
          <i class="mc-fa icon-fill_clock me-2 yellow-500"></i>
          {{ $t('rfidAndFacerecognition.photo.pending') }}
        </span>
        <span
          v-else-if="getFaceRecognitionStatus(data.item) === FACE_RECOGNITION_STATUS_LABEL.INVALID"
        >
          <i class="mc-fa icon-fill_alert-circle me-2 red-500"></i>
          {{ $t('rfidAndFacerecognition.photo.failed') }}
        </span>
        <span v-else>
          <i class="mc-fa icon-fill_close-circle me-2 gray-100"></i>
          {{ $t('rfidAndFacerecognition.photo.empty') }}
        </span>
      </template>
      <template #cell(identification.methods.rfid)="data" v-if="hasScopeRfidAndFaceRecognition">
        {{ (data.item.identification && data.item.identification.methods.rfid) || '-' }}
      </template>

      <template #emptyState>
        <McEmptyState
          class="pb-4"
          :title="$t('maintenance.nolist.noData')"
          :desc="$t('master.service.emptyState.desc')"
          @buttonPrimaryAction="customHandlers.handleAddEmpty"
        />
      </template>
    </McGrapqlTable>
    <b-modal
      id="modal-update-status"
      :title="$t('global.action.changeStatus')"
      size="md"
      centered
      @hidden="resetModal"
    >
      <div class="row p-2">
        <p>
          {{ $t('global.alert.text.changeStatus') }}
          <b>{{ status == 'active' ? $t('global.active') : $t('global.nonActive') }}</b> ?
        </p>
        <b-list-group>
          <b-list-group-item v-for="employee in updateEmployee" :key="employee.userId">
            {{ employee.fullname }}
          </b-list-group-item>
        </b-list-group>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex float-end">
          <McButton variant="secondary" class="me-2" :text="$t('global.no')" @click="cancel()" />
          <McButton variant="primary" :text="$t('global.yes')" @click="toChangeStatus" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McInlineFilter from '@/components/McInlineFilter'
import McFilterSelect from '@/components/McFilterSelect'
import McEmptyState from '@/components/McEmptyState'
import { Helper } from '@/mixin/helper'
import {
  GET_EMPLOYEES,
  GET_DETAIL_EMPLOYEE,
  UPDATE_EMPLOYEE,
  GET_EMPLOYEES_PAGINATED,
} from '@/services/api/master/employees'
import { mapGetters } from 'vuex'
import { getFaceRecognitionStatus, FACE_RECOGNITION_STATUS_LABEL } from '@/helpers/face-recognition'

export default {
  page: {
    title: 'Master',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    McInlineFilter,
    McFilterSelect,
    McEmptyState,
  },
  mixins: [Helper],
  apollo: {
    employees: {
      query: GET_EMPLOYEES,
      result({ data, loading }) {
        if (!loading) {
          this.noData = data.employees.employees.length === 0
          this.isLoading = false
        }
      },
    },
  },
  data() {
    return {
      FACE_RECOGNITION_STATUS_LABEL,
      phaseOneShow: false,
      customHandlers: {
        handleClickRow: (item) => {
          return this.onRowClicked(item)
        },

        handleAddEmpty: () => {
          return this.$router.push({ name: 'Master Employee Create' })
        },
      },
      updateEmployee: [],
      status: '',
      activeFilters: {
        isActive: ['active'],
      },
      noData: false,
      isLoading: true,
    }
  },
  computed: {
    /**
     * Total no. of records
     */
    title() {
      return this.$t('master.karyawan')
    },
    fields() {
      const fields = [
        {
          key: 'fullname',
          label: this.$t('master.form.employee.name'),
          isSortable: true,
          isRedirectable: true,
          isLocked: true,
        },
        {
          key: 'isActive',
          label: this.$t('master.form.employee.jobStatus'),
          isSortable: true,
        },
        {
          key: 'email',
          label: 'Email',
          isSortable: true,
          formatter: (val) => {
            return val ?? '-'
          },
        },
      ]

      if (this.hasScopeRfidAndFaceRecognition) {
        fields.push(
          {
            key: 'identification.methods.faceRecognition',
            label: 'Pengenalan Wajah',
            isSortable: true,
          },
          {
            key: 'identification.methods.rfid',
            label: 'RFID',
            isSortable: true,
          }
        )
      }

      fields.push(
        {
          key: 'position.departmentName',
          label: this.$t('master.form.employee.department'),
          isSortable: false,
          formatter: (val) => {
            return val ?? '-'
          },
        },
        {
          key: 'position.unitName',
          label: this.$t('master.form.employee.unit'),
          isSortable: false,
          formatter: (val) => {
            return val ?? '-'
          },
        },
        {
          key: 'position.name',
          label: this.$t('master.form.employee.position'),
          isSortable: false,
          formatter: (val) => {
            return val ?? '-'
          },
        }
      )

      if (
        this.hasAccessAll([
          'tms-user-group:create',
          'tms-user-group:delete',
          'tms-user-group:read',
          'tms-user-group:update',
        ])
      ) {
        fields.push({
          key: 'groups',
          label: this.$t('master.form.employee.userGroup'),
          isSortable: false,
          formatter: (val) => {
            return val[0]?.name ?? '-'
          },
        })
      }

      return fields
    },
    query() {
      return {
        get: async (filter) => {
          return this.getData(filter)
        },
      }
    },
    filterStatus() {
      return [
        {
          id: 'active',
          label: this.$t('global.active'),
        },
        {
          id: 'inactive',
          label: this.$t('global.nonActive'),
        },
      ]
    },
    hasScopeRfidAndFaceRecognition() {
      return this.hasAccessAll(['d10a-default-rfid&fr:read', 'd10a-default-rfid&fr:update'])
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },
  methods: {
    getFaceRecognitionStatus(item) {
      const hasPhoto = item.photograph != null && item.photograph !== ''
      const faceStatuses = (item.identification?.vehicles || [])
        .map((v) => v?.methodStatuses?.faceRecognition)
        .filter((s) => s != null)
      return getFaceRecognitionStatus({ hasPhoto, faceStatuses })
    },

    async getData(filter) {
      let status = this.activeFilters.isActive
      if (status) {
        status = this.activeFilters.isActive[0] === 'active'
      }
      const res = await this.$apollo.query({
        query: GET_EMPLOYEES_PAGINATED,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
            isActive: status,
          },
        },
      })

      this.isLoading = false

      return res.data.employeesPaginated.employeesPaginated
    },
    onRowClicked(item) {
      if (!this.hasAccessAll(['users:update', 'd5a-default-mdkaryawan:read'])) return

      this.$router.push({
        name: 'Master Employee Detail',
        params: { id: item.userId },
      })
    },
    handleChangeActiveFilters(filters) {
      filters.forEach((filter) => {
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (
          filter.values === null ||
          filter.values === undefined ||
          filter.values === '' ||
          filter.values === false
        )
          return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },
    changeStatus(status, ids) {
      this.status = status
      const selectedIds = Object.entries(ids)
        .filter(([key, value]) => value)
        .map(([key]) => Number(key))

      for (const id of selectedIds) {
        this.$apollo
          .query({
            query: GET_DETAIL_EMPLOYEE,
            variables: {
              filter: {
                userId: parseInt(id),
              },
            },
            fetchPolicy: 'no-cache',
          })
          .then((res) => {
            let isSuccessful = res.data.employee.isSuccessful
            if (isSuccessful) {
              this.updateEmployee.push(res.data.employee.employee)
            }
          })
      }
      this.$bvModal.show('modal-update-status')
    },
    toChangeStatus() {
      let promises = []
      this.isLoading = true
      for (const iterator of this.updateEmployee) {
        promises.push(
          this.$apollo.mutate({
            mutation: UPDATE_EMPLOYEE,
            variables: {
              input: {
                userId: iterator.userId,
                oldData: {
                  isActive: iterator.isActive,
                },
                newData: {
                  isActive: this.status == 'active' ? true : false,
                },
              },
            },
          })
        )
      }

      Promise.all(promises)
        .then((res) => {
          let success = []
          res.forEach((item) => {
            if (item.data.updateEmployee.isSuccessful) {
              success.push(item)
            }
          })

          const successToast = this.$t('master.form.employee.alert.success', {
            number: success.length,
          })

          if (success.length > 0) this.$mcToast.success(successToast)
          else this.$mcToast.error(this.$t('master.form.employee.alert.failed'))
          this.isLoading = false
          this.$bvModal.hide('modal-update-status')
        })
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e)
        })
    },
    resetModal() {
      this.status = null
      this.updateEmployee = []
    },
    cancel() {
      this.$bvModal.hide('modal-update-status')
    },
  },
}
</script>
